import { useParams, useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useState, useRef } from "react";
import { fetchSingleTeam, updateTeam } from "../../actions/International";
import AdminHeader from "../../Components/AdminHeader/AdminHeader";
import AdminNav from "../../Components/AdminNav/AdminNav";
import { IMAGE_URL } from "../../path";

interface FormDataType {
    id: number;
    name: string;
    title: string;
    email_address: string;
    image_path: string;
    display_order: number;
    image: File | null;
    regions: string | null;
}

function EditTeam() {
    const location = useLocation();
    const navigate = useNavigate();
    const user = location.state?.user;
    const { id } = useParams<{ id?: string }>();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [isCollapsed, setIsCollapsed] = useState(true);
    const [teamMember, setTeamMember] = useState<FormDataType>({
        id: 0,
        name: '',
        title: '',
        email_address: '',
        image_path: '',
        display_order: 0,
        image: null,
        regions: '',
    });
    const [imagePreview, setImagePreview] = useState<string | null>(null);

    // Fetch single team member from backend
    const getSingle = () => {
        if (!id) return;

        fetchSingleTeam(Number(id))
            .then(response => {
                const data = response.data.data;
                setTeamMember({
                    id: Number(data.id),
                    name: String(data.name),
                    title: String(data.title),
                    email_address: String(data.email_address),
                    image_path: String(data.image_path),
                    display_order: Number(data.display_order),
                    image: null,
                    regions: data.regions,
                });

                // Initial preview: existing server image
                setImagePreview(data.image_path ? `${IMAGE_URL}${data.image_path}` : null);
            })
            .catch(error => {
                console.error("Failed to fetch single team member:", error);
            });
    };

    useEffect(() => {
        getSingle();
    }, [id]);

    // Handle input changes (including file)
    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value, type } = e.target;

        if (type === "file") {
            const fileInput = e.target as HTMLInputElement;
            const file = fileInput.files?.[0] || null;

            setTeamMember(prev => ({ ...prev, image: file }));

            // Update preview if file selected, otherwise fallback to server image
            if (file) {
                setImagePreview(URL.createObjectURL(file));
            } else {
                setImagePreview(teamMember.image_path ? `${IMAGE_URL}${teamMember.image_path}` : null);
            }
        } else {
            setTeamMember(prev => ({ ...prev, [name]: value }));
        }
    };

    // Handle form submit
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        try {
            const formDataToSend = new FormData();
            formDataToSend.append("name", teamMember.name);
            formDataToSend.append("title", teamMember.title);
            formDataToSend.append("email", teamMember.email_address);
            if (teamMember.regions) {
                formDataToSend.append("regions", teamMember.regions);
            }


            // Only append file if it's a real File object
            if (teamMember.image && teamMember.image instanceof File) {
                formDataToSend.append("image", teamMember.image);
            }

            const response = await updateTeam(Number(id), formDataToSend);

            if (response.status === 200 || response.status === 201) {
                alert("Successfully updated team member!");
                navigate("/dashboard/international/team/list", { state: { user } });
            } else {
                alert(response.data?.message || "An unexpected error occurred");
            }
        } catch (error: any) {
            console.error("Error submitting form:", error);
            if (error.response?.data?.errors) {
                const errorMessages = Object.values(error.response.data.errors).flat();
                alert(errorMessages.join("\n"));
            } else {
                alert("An error occurred while submitting the form");
            }
        }
    };

    return (
        <div>
            <AdminHeader isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} user={user} />
            <AdminNav collapsed={isCollapsed} setCollapsed={setIsCollapsed} user={user} />

            <div className={`main-admin-page ${isCollapsed ? 'collapsed' : 'expanded'}`}>
                <h2>Edit Team Member</h2>
                <form
                    onSubmit={handleSubmit}
                    encType="multipart/form-data"
                    style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
                >
                    <input
                        type="text"
                        placeholder="Name"
                        name="name"
                        value={teamMember.name}
                        onChange={handleInputChange}
                        required
                    />

                    <input
                        type="text"
                        placeholder="Title"
                        name="title"
                        value={teamMember.title}
                        onChange={handleInputChange}
                        required
                    />
                    <input
                        type="text"
                        placeholder="Regions"
                        name="regions"
                        value={teamMember.regions ?? ''}
                        onChange={handleInputChange}
                        required
                    />

                    <input
                        type="file"
                        name="image"
                        ref={fileInputRef}
                        accept="image/png, image/jpeg, image/jpg, image/gif, image/svg+xml"
                        onChange={handleInputChange}
                    />

                    {/* Image preview */}
                    {imagePreview && (
                        <div style={{ marginTop: "10px" }}>
                            <img
                                src={imagePreview}
                                alt="Preview"
                                style={{ maxWidth: "300px", maxHeight: "300px", objectFit: "cover" }}
                            />
                        </div>
                    )}

                    <input
                        type="text"
                        placeholder="Email Address"
                        name="email_address"
                        value={teamMember.email_address}
                        onChange={handleInputChange}
                        required
                    />

                    <div className="col-md-12 col-sm-12 col-xs-12">
                        <button type="submit" className="btn btn-success">
                            Submit
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default EditTeam;
